import React from "react";

/**
 * The one wrapper every page stands in.
 *
 * Nine pages independently grew the same header — a `text-micro` uppercase
 * eyebrow, an `h1 text-3xl font-extrabold`, and a `text-sm` subtitle — sitting
 * inside `p-4 md:p-6 lg:p-8 max-w-7xl mx-auto space-y-6`. On a 1080p floor
 * monitor that header spent roughly 130 vertical pixels before the first row of
 * actual stock, and the AppShell topbar was already printing the page name two
 * inches above it (`data-testid="current-page-title"`). The eyebrow said
 * INVENTORY CONTROL under a topbar that said Inventory.
 *
 * Brand bible §2.2 bans oversized hero headers and decorative whitespace; §58
 * asks for minimal decorative whitespace and compact rows. So there is no hero
 * here to configure. What is left is a single line: an optional context title
 * and the page's actions, on one row, at one height.
 *
 * ONE padding value. Not `p-4 md:p-6 lg:p-8` — a responsive escalation that
 * gives the widest screen, the one with the most rows to show, the least room
 * to show them in.
 */

const WIDTHS = {
  // Dense operational default. A floor manager on a wide monitor bought that
  // width to see more tables at once, not to see a wider margin.
  full: "",
  // Opt-in for pages that are genuinely read rather than scanned.
  contained: "max-w-7xl mx-auto w-full",
};

export function PageShell({
  title,
  actions,
  caption,
  width = "full",
  children,
  className = "",
  testId,
}) {
  const hasHead = Boolean(title || actions);
  const hasHeader = hasHead || Boolean(caption);

  return (
    <div
      className={`flex flex-col h-full min-w-0 w-full p-loose gap-section ${WIDTHS[width] ?? WIDTHS.full} ${className}`.trim()}
      data-testid={testId}
    >
      {hasHeader && (
        /*
         * Title row and caption are one group held at `gap-snug`, so the
         * caption reads as belonging to the row above it while the outer
         * `gap-section` still separates the whole header from the content. A
         * negative margin would do the same thing and then misfire on a page
         * that passes a caption and no title.
         */
        <div className="flex flex-col gap-snug shrink-0">
          {hasHead && (
            /*
             * Single line, and it stays a single line: the title truncates
             * rather than wrapping and pushing the actions down onto a second
             * row, which is how a "compact" header silently becomes a tall one
             * on a narrow tablet. `min-w-0` is what lets truncate actually bite
             * inside flex.
             */
            <div
              className="flex flex-wrap sm:flex-nowrap items-center justify-between gap-base"
              data-testid={testId ? `${testId}-head` : "page-shell-head"}
            >
              {title ? (
                <h2 className="text-lede font-semibold truncate min-w-0" data-testid="page-shell-title">
                  {title}
                </h2>
              ) : (
                <span aria-hidden="true" />
              )}
              {actions && (
                // Phones: the actions wrap and right-align rather than run off the
                // screen (owner, 2026-10-25); from md up they never shrink.
                <div className="flex flex-wrap items-center justify-end gap-snug min-w-0 md:shrink-0" data-testid="page-shell-actions">
                  {actions}
                </div>
              )}
            </div>
          )}

          {/*
           * Real domain warnings only — "on hand is derived from the movement
           * ledger, never a stored total", "this figure is cut at the 3am
           * business day". Brand bible §47: never hide uncertainty. Not a
           * subtitle slot for restating the page name, and it renders nothing
           * at all when empty so there is no reserved band of blank space
           * waiting to be filled.
           */}
          {caption && (
            <p className="text-meta" data-testid="page-shell-caption">
              {caption}
            </p>
          )}
        </div>
      )}

      {/*
       * `min-h-0` and `min-w-0` are load-bearing: without them a flex child refuses to shrink
       * below its content dimensions, causing overflow.
       */}
      <div className="flex-1 min-h-0 min-w-0 w-full flex flex-col gap-section">{children}</div>
    </div>
  );
}

export default PageShell;
